<script lang="ts">
  import { BUTTON_VARIANT, BUTTON_SIZE } from '$lib/constants/constants';
  import type { ElementAttributes } from '$lib/types/contentTypes';

  export let text: string | undefined = '';
  export let variant: string | undefined = BUTTON_VARIANT.CUSTOM;
  export let buttonSize: string | undefined = BUTTON_SIZE.CUSTOM;
  export let url: string | undefined = undefined;
  export let openInNewTab: boolean | undefined = false;
  export let dataTestId: string | undefined = undefined;
  export let id: string | undefined = undefined;

  export let disabled: boolean | undefined = false;
  export let customClass: string | undefined = '';
  export let onClick: (...args: any[]) => void = () => {};
  export let customStyle: string | null = null;
  let trackingClass = '';
  export let plausibleClass:
    | 'email_signup'
    | 'facebook_signup'
    | 'facebook_account'
    | 'email_account'
    | 'pay'
    | string = '';

  let buttonClass =
    'border font-semibold rounded-full flex flex-row justify-center items-center focus:outline-0 focus:shadow-button';
  let sizeClass = '';
  let variantClass = '';

  $: tag = url ? 'a' : 'button';
  $: href = url ? url : '';
  $: target = openInNewTab ? '_blank' : '_self';
  $: role = url ? 'link' : 'button';

  // This is for attributes which are not on all html elements. (e.g. disabled can only be used on button)
  $: attributes = {
    disabled,
  } as ElementAttributes;

  $: switch (buttonSize) {
    case BUTTON_SIZE.LARGE:
      sizeClass = 'px-2 md:px-8 py-2.5 text-md';
      break;
    case BUTTON_SIZE.MEDIUM:
      sizeClass = 'px-2 md:px-6 py-2.5 text-sm';
      break;
  }

  $: switch (variant) {
    case BUTTON_VARIANT.PRIMARY:
      variantClass =
        'bg-primary-500 border-primary-500 text-white hover:bg-primary-700 hover:border-primary-700 focus:primary-shadow disabled:bg-gray-100 disabled:border-gray-100 disabled:text-gray-300';
      break;
    case BUTTON_VARIANT.OUTLINED:
      variantClass =
        'bg-white border-gray-500 text-gray-700 hover:bg-gray-50 hover:text-gray-800 focus:shadow-gray-100/95 disabled:text-gray-300 disabled:border-gray-200';
      break;
    case BUTTON_VARIANT.SECONDARY:
      variantClass =
        'bg-aubergine-button border-aubergine-button text-white hover:bg-aubergine-hover hover:border-aubergine-hover focus:shadow-aubergine-shadow disabled:bg-gray-100 disabled:border-gray-100 disabled:text-gray-300';
      break;
    case BUTTON_VARIANT.FACEBOOK:
      variantClass =
        'bg-[#1877F2] border-[#1877F2] text-white hover:bg-[#0c64d6] hover:border-[#0c64d6] disabled:bg-gray-100 disabled:border-gray-100 disabled:text-gray-300';
  }

  $: switch (plausibleClass) {
    case 'email_signup':
      trackingClass = 'plausible-event-name=new_paywall_signup_email';
      break;
    case 'facebook_signup':
      trackingClass = 'plausible-event-name=new_paywall_signup_facebook';
      break;
    case 'facebook_account':
      trackingClass = 'plausible-event-name=new_paywall_create_facebook_account';
      break;
    case 'email_account':
      trackingClass = 'plausible-event-name=new_paywall_create_email_account';
      break;
    case 'pay':
      trackingClass = 'plausible-event-name=new_paywall_pay';
      break;
  }
  // TODO: move plauisble logic outside of button component
  let plausible =
    window.plausible ||
    function () {
      (window.plausible.q = window.plausible.q || []).push(arguments);
    };

  function createAccount() {
    let emailBtn = document.querySelector('[data-testid="create-account"]');
    let fbBtn = document.querySelector('[aria-label="Continue with Facebook"]');
    emailBtn?.addEventListener('click', function () {
      plausible('new_paywall_create_email_account');
    });
    fbBtn?.addEventListener('click', function () {
      plausible('new_paywall_create_facebook_account');
    });
  }

  function callBack(e) {
    if (onClick) {
      onClick(e);
      createAccount();
    }
  }
</script>

<svelte:element
  this={tag}
  {id}
  class="{trackingClass} {buttonClass} {sizeClass} {variantClass} {customClass}"
  {href}
  {target}
  {...attributes}
  {role}
  aria-label={text}
  on:click={callBack}
  style={customStyle}
  data-testid={dataTestId}
>
  <span class="mr-2">
    <slot name="left" />
  </span>
  {text}
  <span class="ml-2">
    <slot name="right" />
  </span>
</svelte:element>

<style>
  #ot-sdk-btn.ot-sdk-show-settings,
  #ot-sdk-btn.optanon-show-settings {
    border: 1px solid #ff1a66;
  }
  #ot-sdk-btn.ot-sdk-show-settings:hover,
  #ot-sdk-btn.optanon-show-settings:hover {
    background-color: #e5004d;
    border: 1px solid #e5004d;
  }
</style>
